/*
 * LW (2026-10-19): a small "Or call us now · (204) 717-0458" link beside every free-mockup button while the shop
 * is open, and "Or email us · sales@leatherwood.ca" after hours / on closed days. The mockup form stays the main
 * action: this is a secondary text link under (or beside) each mockup button, never a replacement.
 * - Config: data-cn-* on the Mockup form block wrapper (blocks/lw-mockup-form.liquid, "Call us (business hours)").
 * - Open / closed: the shop's local time via Intl.DateTimeFormat({ timeZone }), re-checked every minute, so an open
 *   page swaps call <-> email at opening / closing time.
 * - Buttons: links to #form-jump and the drawer-only .lw-form__open button. Skipped: header, footer, menus, the
 *   jump-nav bar, dialogs/drawers, the Globo form itself, fixed bars, plain text links (e.g. "Ask for a custom quote").
 *   Buttons that share a parent get one link after the last of them.
 * - Testing only: ?lw_hours=open / ?lw_hours=closed forces the state.
 * - Tracking: GA4 call_click / email_click { page_path, lw_view, placement } via gtag (theme stub -> dataLayer).
 *   No Meta pixel event on purpose (lwFormContext.track would also send fbq).
 */
(() => {
  if (window.__lwCallNow) return;
  window.__lwCallNow = true;

  const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const CTA = 'a[href$="#form-jump"], .lw-form__open';
  const SKIP = 'header, footer, nav, header-component, .header, .footer, .shopify-section-group-header-group, .shopify-section-group-footer-group, .lwp-jn, .lw-drawer, .lwm-drawer, dialog, [role="dialog"], [aria-modal="true"], .globo-formbuilder, .lw-call-now-row';
  const ICON_CALL = '<svg aria-hidden="true" focusable="false" width="15" height="15" viewBox="0 0 24 24"><path fill="currentColor" d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.6a1 1 0 0 1-.25 1z"/></svg>';
  const ICON_MAIL = '<svg aria-hidden="true" focusable="false" width="15" height="15" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" d="M3 5.5h18v13H3z"/><path fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" d="m3.5 6 8.5 7 8.5-7"/></svg>';

  // "Mon-Fri", "Mon–Thu, Sat", "Mon,Wed,Fri", "Fri-Mon", "daily" -> Set of day numbers (0 = Sunday).
  const parseDays = (str) => {
    const set = new Set();
    for (const tok of String(str || '').toLowerCase().split(/[,;/&]+|\band\b/)) {
      const t = tok.trim();
      if (!t) continue;
      if (/^(daily|every ?day|all|7 days)/.test(t)) { DAYS.forEach((_, i) => set.add(i)); continue; }
      const [x, y] = t.split(/\s*(?:-|–|—|\bto\b|\bthrough\b|\bthru\b)\s*/);
      const a = DAYS.indexOf((x || '').slice(0, 3));
      const b = y ? DAYS.indexOf(y.slice(0, 3)) : a;
      if (a < 0 || b < 0) continue;
      for (let i = a; ; i = (i + 1) % 7) { set.add(i); if (i === b) break; }
    }
    return set;
  };
  // "08:00", "8", "16:30", "4:30pm" -> minutes since midnight.
  const mins = (v, def) => {
    const m = String(v || '').trim().toLowerCase().match(/^(\d{1,2})(?:[:.h](\d{2}))?\s*(a|p)?\.?m?\.?$/);
    if (!m) return def == null ? null : mins(def);
    let h = +m[1] % 24;
    if (m[3] === 'p' && h < 12) h += 12;
    if (m[3] === 'a' && h === 12) h = 0;
    return h * 60 + Math.min(59, +(m[2] || 0));
  };

  const read = () => {
    const el = document.querySelector('[data-lw-cn]');
    if (!el) return null;
    const d = el.dataset;
    const days = parseDays(d.cnDays || 'Mon-Fri');
    return {
      call: d.cnCall === '1' && !!d.cnTel && !!d.cnPhone,
      phone: d.cnPhone,
      tel: d.cnTel,
      label: d.cnLabel || 'Or call us now',
      days: days.size ? days : parseDays('Mon-Fri'),
      open: mins(d.cnOpen, '08:00'),
      close: mins(d.cnClose, '16:30'),
      tz: d.cnTz || 'America/Winnipeg',
      mail: (d.cnMail || '').trim(),
      mailLabel: d.cnMailLabel || 'Or email us',
    };
  };

  const localNow = (tz) => {
    const fmt = (zone) => new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }).formatToParts(new Date());
    let parts;
    try { parts = fmt(tz); } catch (e) { parts = fmt('America/Winnipeg'); }
    const get = (type) => (parts.find((p) => p.type === type) || {}).value || '';
    return { day: DAYS.indexOf(get('weekday').toLowerCase().slice(0, 3)), m: (+get('hour') % 24) * 60 + +get('minute') };
  };

  const force = (() => { try { return new URLSearchParams(location.search).get('lw_hours'); } catch (e) { return null; } })();
  const isOpen = (c) => {
    if (force === 'open') return true;
    if (force === 'closed') return false;
    const { day, m } = localNow(c.tz);
    if (c.close > c.open) return c.days.has(day) && m >= c.open && m < c.close;
    // Past midnight (e.g. 18:00-02:00): the early hours belong to the day before.
    return (c.days.has(day) && m >= c.open) || (c.days.has((day + 6) % 7) && m < c.close);
  };

  const esc = (s) => String(s).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);
  const html = (mode, c) => {
    const [href, icon, label, value] = mode === 'call'
      ? [c.tel, ICON_CALL, c.label, c.phone]
      : ['mailto:' + c.mail, ICON_MAIL, c.mailLabel, c.mail];
    return `<a class="lw-call-now${mode === 'email' ? ' lw-call-now--email' : ''}" href="${esc(href)}">${icon}` +
      `<span class="lw-call-now__label">${esc(label)}</span><span class="lw-call-now__sep" aria-hidden="true">·</span>` +
      `<span class="lw-call-now__value">${esc(value)}</span></a>`;
  };

  // Mockup buttons only: a button-looking link, not inside chrome / bars / dialogs, not in a fixed (or short sticky) bar.
  const eligible = (a) => {
    if (a.closest(SKIP)) return false;
    if (!a.matches('.button, .lw-btn, .lws-btn, .lw-form__open, [class*="button"]')) return false;
    for (let e = a; e && e !== document.body; e = e.parentElement) {
      const p = getComputedStyle(e).position;
      if (p === 'fixed' || (p === 'sticky' && e.offsetHeight < 160)) return false;
    }
    return true;
  };

  const rows = new Map(); // parent element -> { row, anchor }

  // Text colour: the link inherits from its container, but a hero over a photo sets light text on its own text blocks
  // while the container stays dark. Take the colour of the nearest plain copy before the button in the same section:
  // its block-level colour (not a red <strong>), skipping copy inside its own boxed callout (a tinted note card).
  const boxed = (el, stop) => {
    for (let e = el; e && e !== stop && !e.contains(stop); e = e.parentElement) {
      const cs = getComputedStyle(e);
      if (cs.backgroundImage !== 'none' || !/^(transparent|rgba\(0, 0, 0, 0\))$/.test(cs.backgroundColor)) return true;
    }
    return false;
  };
  const textColour = (anchor, box) => {
    const sec = anchor.closest('.shopify-section') || document.body;
    const w = document.createTreeWalker(sec, NodeFilter.SHOW_TEXT);
    w.currentNode = anchor;
    for (let n = w.previousNode(), i = 0; n && i < 400; n = w.previousNode(), i++) {
      let el = n.parentElement;
      if (!n.nodeValue.trim() || !el || el.closest('a, button, script, style, .lw-call-now-row, [hidden]')) continue;
      while (el.parentElement && el !== sec && getComputedStyle(el).display.startsWith('inline')) el = el.parentElement;
      const cs = getComputedStyle(el);
      if (!el.getClientRects().length || cs.visibility === 'hidden' || boxed(el, box)) continue;
      return cs.color;
    }
    return '';
  };

  // Where the row sits: under the button (after the last of a group). A row of buttons gets the link on its own line
  // after the whole row (inside it when it wraps, after it when it does not: never squeezing the buttons).
  // Alignment follows the buttons: centred under a full-width or centred button / group, else left (or right).
  const place = (rec, insert) => {
    const { row, anchor } = rec;
    const parent = anchor.parentElement;
    const ps = getComputedStyle(parent);
    const flexRow = ps.display.includes('flex') && ps.flexDirection.startsWith('row') && parent.children.length > 1;
    if (insert) {
      if (flexRow && ps.flexWrap !== 'nowrap') parent.appendChild(row);
      else if (flexRow) parent.after(row);
      else anchor.after(row);
    }
    row.classList.toggle('lw-call-now-row--break', row.parentElement === parent && flexRow);
    // Box of the buttons: the anchor, or the whole row of buttons.
    let ar = anchor.getBoundingClientRect();
    if (flexRow) {
      const rs = [...parent.children].filter((k) => k !== row && k.getClientRects().length).map((k) => k.getBoundingClientRect());
      if (rs.length) ar = { left: Math.min(...rs.map((r) => r.left)), right: Math.max(...rs.map((r) => r.right)) };
      ar.width = ar.right - ar.left;
    }
    const box = row.parentElement;
    const bs = getComputedStyle(box);
    const br = box.getBoundingClientRect();
    const pl = br.left + parseFloat(bs.paddingLeft || 0);
    const pw = br.width - parseFloat(bs.paddingLeft || 0) - parseFloat(bs.paddingRight || 0);
    let j = 'flex-start';
    if (ar.width >= pw * 0.9 || Math.abs(ar.left + ar.width / 2 - (pl + pw / 2)) < 6) j = 'center';
    else if (ar.left - pl > pw / 2) j = 'flex-end';
    row.style.setProperty('--lw-cn-justify', j);
    // In a stretched column the row is full width and justify places the link; else align-self does.
    row.style.alignSelf = bs.display.includes('flex') && bs.flexDirection.startsWith('column') ? 'stretch' : '';
    const col = textColour(anchor, box);
    if (col) row.style.color = col;
    row.hidden = !anchor.getClientRects().length;
  };

  const refresh = () => {
    const c = read();
    const mode = !c ? null : isOpen(c) ? (c.call ? 'call' : null) : (c.mail ? 'email' : null);
    // Off: remove every link (nothing reserved).
    if (!mode) { rows.forEach((r) => r.row.remove()); rows.clear(); return; }
    for (const [parent, r] of rows) if (!r.row.isConnected || !r.anchor.isConnected) { r.row.remove(); rows.delete(parent); }
    const last = new Map();
    document.querySelectorAll(CTA).forEach((a) => { if (a.parentElement && eligible(a)) last.set(a.parentElement, a); });
    for (const [parent, anchor] of last) {
      let r = rows.get(parent);
      if (r && r.anchor !== anchor) { r.row.remove(); r = null; }
      if (!r) {
        const row = document.createElement('div');
        row.className = 'lw-call-now-row';
        row.dataset.placement = (parent.closest('.shopify-section')?.id || '').replace(/^shopify-section-(template--\d+__)?/, '');
        r = { row, anchor };
        rows.set(parent, r);
        r.row.innerHTML = html(mode, c);
        r.row.dataset.mode = mode;
        place(r, true);
        continue;
      }
      if (r.row.dataset.mode !== mode) { r.row.innerHTML = html(mode, c); r.row.dataset.mode = mode; }
      place(r, false);
    }
  };

  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element ? e.target.closest('.lw-call-now') : null;
    if (!a) return;
    const params = {
      page_path: location.pathname,
      lw_view: (window.lwPage && window.lwPage.template) || '',
      placement: a.closest('.lw-call-now-row')?.dataset.placement || '',
    };
    try { if (typeof window.gtag === 'function') window.gtag('event', a.classList.contains('lw-call-now--email') ? 'email_click' : 'call_click', params); } catch (err) {}
  });

  refresh();
  // Sections that render their buttons late (e.g. the offer stack hydrates when scrolled near): rescan when a
  // mockup button is added. Mutations inside the Globo form are ignored (it re-renders a lot).
  let queued = false;
  new MutationObserver((recs) => {
    if (queued) return;
    for (const rec of recs) {
      if (rec.target.closest && rec.target.closest('.globo-formbuilder, .lw-call-now-row')) continue;
      for (const n of rec.addedNodes) {
        if (n.nodeType === 1 && (n.matches(CTA) || n.querySelector(CTA))) {
          queued = true;
          requestAnimationFrame(() => { queued = false; refresh(); });
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
  // Re-check on the minute (swaps call <-> email at opening / closing time), when the tab comes back, and on resize
  // (alignment). Theme editor: sections re-render, so rescan.
  setTimeout(() => { refresh(); setInterval(refresh, 60000); }, 60000 - (Date.now() % 60000) + 50);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  let rz;
  window.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(refresh, 150); });
  ['shopify:section:load', 'shopify:section:unload', 'shopify:block:select'].forEach((ev) => document.addEventListener(ev, () => setTimeout(refresh, 0)));
  window.lwCallNow = { refresh };
})();
